import React, { useState } from 'react';
import cx from 'classnames';

import { LANDING_PAGE_EVENTS } from 'Utilities/analytics';

import { Content } from 'LegacySBCRA/Content/Content';

import { ReactComponent as PlayIcon } from '../../../../../../client/assets_seller/landingpages/sell_my_car/icon-youtube.svg';
import { usePageRefEvents } from '../../../common/Hooks/usePageRefEvents';
import { type RoundedSectionType, RoundedSection } from '../../../common/RoundedSection/RoundedSection';
import type { ContentBlock as ContentBlockType, ContentBlockRow, ContentBlockRowTheme } from '../../@types/landingPages';
import { landingPageSectionLoadEvent, PAGE_SECTIONS } from '../../Pages/LandingPage.helpers';

import { getYouTubeEmbedUrl, toImageSrc } from './ContentBlock.helpers';

import styles from './ContentBlock.scss';

const ROW_THEME_CLASS: Record<ContentBlockRowTheme, string> = {
  black: styles.rowBlack,
  grey: styles.rowGrey,
  white: styles.rowWhite,
};

type ContentBlockProps = {
  content?: ContentBlockRow[];
  layout?: ContentBlockType['layout'];
  theme?: RoundedSectionType;
};

type ContentCardProps = {
  block: ContentBlockRow;
  isRowLayout: boolean;
};

const ContentCard = ({ block, isRowLayout }: ContentCardProps) => {
  const {
    copy, image, image_link: imageLink, theme,
  } = block;

  const [showVideo, setShowVideo] = useState(false);

  const embedUrl = getYouTubeEmbedUrl(imageLink);
  const isVideo = embedUrl !== null;

  const renderVideoEmbed = () => (
    <iframe
      allowFullScreen
      allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      className={styles.videoEmbed}
      data-testid="youtube-vid-player"
      referrerPolicy="strict-origin-when-cross-origin"
      src={embedUrl ?? undefined}
      title={image?.alt?.trim() || 'YouTube video player'}
    />
  );

  const renderMedia = () => {
    if (!image?.url) {
      return isVideo ? <div className={styles.imageWrapper}>{renderVideoEmbed()}</div> : null;
    }

    const imgEl = (
      <img
        alt={image.alt ?? ''}
        className={styles.image}
        loading="lazy"
        src={toImageSrc(image.url)}
      />
    );

    let media = imgEl;

    if (isVideo && showVideo) {
      media = renderVideoEmbed();
    } else if (isVideo) {
      media = (
        <button
          aria-label={`Play ${image.alt ? `${image.alt} ` : ''}video`}
          className={styles.videoButton}
          onClick={() => {
            setShowVideo(true);
            LANDING_PAGE_EVENTS.CONTENT_CLICK({ label: 'video', url: embedUrl ?? '' });
          }}
          type="button"
        >
          {imgEl}
          <PlayIcon aria-hidden className={styles.playIcon} />
        </button>
      );
    } else if (imageLink) {
      media = (
        <a
          href={imageLink}
          onClick={() => LANDING_PAGE_EVENTS.CONTENT_CLICK({ label: 'link', url: imageLink })}
          rel="noopener noreferrer"
          target="_blank"
        >
          {imgEl}
        </a>
      );
    }

    return <div className={styles.imageWrapper}>{media}</div>;
  };

  return (
    <div className={cx(styles.content, ROW_THEME_CLASS[theme], { [styles.row]: isRowLayout })}>
      {renderMedia()}
      {copy ? (
        <div dangerouslySetInnerHTML={{ __html: copy }} className={styles.copy} />
      ) : null}
    </div>
  );
};

export const ContentBlock = ({ content = [], layout = 'row', theme }: ContentBlockProps) => {
  const { setRef } = usePageRefEvents({
    onRefTrigger: () => landingPageSectionLoadEvent(PAGE_SECTIONS.CONTENT),
  });

  const validContentBlocks = content.filter((block) => block?.copy || block?.image?.url);

  if (!validContentBlocks.length) {
    return null;
  }

  if (layout === 'column') {
    const [firstBlock, ...restBlocks] = validContentBlocks;

    return (
      <RoundedSection withBottomLedge withBottomPadding withTopLedge theme={theme}>
        <Content>
          <div ref={(ref) => setRef(ref)} className={styles.rowsColumn}>
            <ContentCard block={firstBlock} isRowLayout={false} />
            {restBlocks.length ? (
              <div className={styles.columnStack}>
                {restBlocks.map((block, index) => (
                  <ContentCard key={`${block.theme}-${index}`} block={block} isRowLayout={false} />
                ))}
              </div>
            ) : null}
          </div>
        </Content>
      </RoundedSection>
    );
  }

  return (
    <RoundedSection withBottomLedge withBottomPadding withTopLedge theme={theme}>
      <Content>
        <div ref={(ref) => setRef(ref)} className={styles.rows}>
          {validContentBlocks.map((block, index) => (
            <ContentCard key={`${block.theme}-${index}`} isRowLayout block={block} />
          ))}
        </div>
      </Content>
    </RoundedSection>
  );
};
